/* eslint-disable complexity */
import {
  Component,
  OnDestroy,
  ViewContainerRef,
} from '@angular/core'
import { UntypedFormGroup } from '@angular/forms'
import { Subscription } from 'rxjs'
import {
  ASSET_CLASS_FILTERS,
  CREDIT_QUALITY_FILTERS,
  FILTER_MAP,
  MARKET_CAPITALIZATION_FILTERS,
  MATURITY_FILTERS,
  STYLE_FILTERS,
} from 'src/app/models/filter-details.model'
import { Entity } from 'src/app/models/fund-details.model'
import { AEMChunk } from 'src/app/services/AEM/aem-chunk.model'
import { CentralContentService } from 'src/app/services/content/central-content.service'
import { FiltersService } from 'src/app/services/filtersService.service'

@Component({
  selector: 'asset-class-filter',
  templateUrl: './assetClassFilter.component.html',
  styleUrls: ['./assetClassFilter.component.scss'],
  providers: [],
})
export class AssetClassFilterComponent implements OnDestroy {
  // AEM content Vars
  assetClass: AEMChunk
  creditQuality: AEMChunk
  maturity: AEMChunk
  marketCapitalization: AEMChunk
  style: AEMChunk
  aemContentSub: Subscription
  asset: string
  description: string
  dialogTitle: string
  popupDescription: AEMChunk
  STRATEGY_FUND = '1298'
  withoutAsetClassFilteredData: Entity[]
  // eslint-disable-next-line max-params
  constructor(
    private readonly filtersService: FiltersService,
    private readonly viewContainer: ViewContainerRef,
    private readonly centralContentService: CentralContentService,
  ) {}

  public readonly assetClassFilterLabels: string[] = ASSET_CLASS_FILTERS
  public readonly marketCapitalFilterLabels: string[] = MARKET_CAPITALIZATION_FILTERS
  public readonly styleFilterLabels: string[] = STYLE_FILTERS
  public readonly creditQualityFilterLabels: string[] = CREDIT_QUALITY_FILTERS
  public readonly maturityFilterLabels: string[] = MATURITY_FILTERS
  public readonly filterMap = FILTER_MAP

  public assetClassGroup: UntypedFormGroup
  public assetClassMarketCapGroup: UntypedFormGroup[]
  public assetClassStyleGroup: UntypedFormGroup[]
  public assetClassCreditQualityGroup: UntypedFormGroup[]
  public assetClassMaturityGroup: UntypedFormGroup[]

  // eslint-disable-next-line max-statements
  ngOnInit() {
    this.assetClassGroup = this.filtersService.activeFilters.assetClass
    this.assetClassGroup.valueChanges.subscribe(() => {
      this.filtersService.applyFilters()
    })

    this.assetClassMarketCapGroup = this.filtersService.activeFilters.assetClassMarketCap
    this.assetClassStyleGroup = this.filtersService.activeFilters.assetClassStyle
    this.assetClassCreditQualityGroup = this.filtersService.activeFilters.assetClassCredit
    this.assetClassMaturityGroup = this.filtersService.activeFilters.assetClassMaturity

    this.assetClassMarketCapGroup['equity'].valueChanges.subscribe(() => {
      this.filtersService.applyFilters()
    })

    this.assetClassStyleGroup['equity'].valueChanges.subscribe(() => {
      this.filtersService.applyFilters()
    })

    this.assetClassCreditQualityGroup['fixed_income'].valueChanges.subscribe(
      () => {
        this.filtersService.applyFilters()
      },
    )

    this.assetClassMaturityGroup['fixed_income'].valueChanges.subscribe(() => {
      this.filtersService.applyFilters()
    })

    this.filtersService.withoutAsetClassFilteredData$.subscribe(
      (filteredResults) => {
        if (filteredResults !== null && filteredResults !== undefined) {
          this.withoutAsetClassFilteredData = filteredResults.fund?.entity
        }
      },
    )
    this.aemContentSub = this.centralContentService.aemContent$.subscribe(
      (content) => {
        this.parseAEMContent(content)
      },
    )
  }

  parseAEMContent(content) {
    {
      this.assetClass = this.centralContentService.getContentByBody(
        'fund_definitions_whats_an_asset_class',
        content,
      )
      this.creditQuality = this.centralContentService.getContentByBody(
        'fund_definitions_credit_quality',
        content,
      )
      this.maturity = this.centralContentService.getContentByBody(
        'fund_definitions_maturity',
        content,
      )
      this.marketCapitalization = this.centralContentService.getContentByBody(
        'fund_definitions_market_capitalization',
        content,
      )
      this.style = this.centralContentService.getContentByBody(
        'fund_definitions_style',
        content,
      )
    }
    //this needs an initial value since the modal content is loaded on view init.
    this.popupDescription = this.assetClass;
  }
  handleAssetClass(event, assetClass) {
    if (!event.target.checked) {
      if (assetClass === 'equity') {
        MARKET_CAPITALIZATION_FILTERS.forEach((market) => {
          this.assetClassMarketCapGroup['equity'].controls[market].setValue(
            false,
          )
        })
        STYLE_FILTERS.forEach((style) => {
          this.assetClassStyleGroup['equity'].controls[style].setValue(false)
        })
      }
      if (assetClass === 'fixed_income') {
        CREDIT_QUALITY_FILTERS.forEach((credit) => {
          this.assetClassCreditQualityGroup['fixed_income'].controls[
            credit
          ].setValue(false)
        })
        MATURITY_FILTERS.forEach((maturity) => {
          this.assetClassMaturityGroup['fixed_income'].controls[
            maturity
          ].setValue(false)
        })
      }
    }
  }

  isAssetClassSelected(value) {
    if (this.filtersService.activeFilters.assetClass.controls[value].value) {
      return true
    }
    return false
  }

  setValueForMarketStyle(value) {
    MARKET_CAPITALIZATION_FILTERS.forEach((market) => {
      this.assetClassMarketCapGroup['equity'].controls[market].setValue(value)
    })
    STYLE_FILTERS.forEach((style) => {
      this.assetClassStyleGroup['equity'].controls[style].setValue(value)
    })
  }
  setValueForCreditMaturity(value) {
    CREDIT_QUALITY_FILTERS.forEach((credit) => {
      this.assetClassCreditQualityGroup['fixed_income'].controls[
        credit
      ].setValue(value)
    })
    MATURITY_FILTERS.forEach((maturity) => {
      this.assetClassMaturityGroup['fixed_income'].controls[maturity].setValue(
        value,
      )
    })
  }

  countByAssetClass(assetClass) {
    const afterFiltered = this.withoutAsetClassFilteredData?.filter(
      (fund) =>
        (assetClass === 'equity' &&
          fund.profile.fundFact.isStock === true &&
          fund.profile.fundFact.isSectorSpecific === false &&
          fund.profile.fundFact.isSpecialtyStockFund === false &&
          fund.profile.fundId !== this.STRATEGY_FUND &&
          fund.profile?.category !== 'Long-Short' &&
          this.filtersService.filterByMarketCap('equity', fund) &&
          this.filtersService.filterByStyle('equity', fund)) ||
        (assetClass === 'fixed_income' &&
          fund.profile.fundFact.isBond === true &&
          this.filtersService.filterByMaturity('fixed_income', fund) &&
          this.filtersService.filterByCredit('fixed_income', fund)) ||
        (assetClass === 'money_market' &&
          fund.profile.fundFact.isMoneyMarket === true) ||
        (assetClass === 'multi_asset' &&
          fund.profile.fundFact.isBalanced === true) ||
        (assetClass === 'sector_specialty' &&
          (fund.profile.fundFact.isSectorSpecific === true ||
            fund.profile.fundFact.isSpecialtyStockFund === true ||
            fund.profile?.category === 'Long-Short'))
    )
    return afterFiltered?.length || 0
  }

  public setDialogInfo(title) {
    this.dialogTitle = title;
    if (this.dialogTitle === 'Asset class') {
      this.dialogTitle = "What's an asset class?"
      this.popupDescription = this.assetClass
    }
    if (this.dialogTitle === 'Market capitalization') {
      this.popupDescription = this.marketCapitalization
    }
    if (this.dialogTitle === 'Style') {
      this.popupDescription = this.style
    }
    if (this.dialogTitle === 'Credit Quality') {
      this.popupDescription = this.creditQuality
    }
    if (this.dialogTitle === 'Maturity') {
      this.popupDescription = this.maturity
    }
  }

  replaceSpecialChars(value: string) {
    return value.replace(/&/g, 'and').replace(/ /g, '-')
  }

  formatFilterValue(val) {
    if (val === 'sector_specialty') {
      return val.replace(/_/g, ' & ')
    } else if(val==="equity"){
      return 'Stocks'
    } else if(val==="multi_asset"){
      return 'Balanced'
    }else if (val === 'us') {
      return 'US'
    } else {
      return val.replace(/_/g, ' ')
    }
  }

  formatLink(str) {
    const from = ['&lt;', '&gt;', '&amp;', '&quot;', '>nbsp;']
    const to = ['<', '>', '&', '"', ' ']
    from.forEach((data, index) => {
      str = this.regularExpression(str, data, to[index])
    })
    return str
  }
  regularExpression(str, find, replace) {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    const escapedFind = find.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, '\\$1')
    return str.toString().replace(new RegExp(escapedFind, 'g'), replace)
  }
  ngOnDestroy(): void {
    this.aemContentSub.unsubscribe()
  }
}
